import React, { useEffect, useState, useCallback } from "react";
import { Gift, Plus, Trash2, Check, X, Coins } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../ui/dialog";

export const LoyaltyTab = () => {
  const [members, setMembers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, r, d, t] = await Promise.all([
        api.get("/admin/members"),
        api.get("/admin/rewards"),
        api.get("/admin/redemptions"),
        api.get("/admin/points"),
      ]);
      setMembers(m.data); setRewards(r.data); setRedemptions(d.data); setTxns(t.data);
    } catch (e) { toast.error("Failed to load loyalty data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) return <p className="text-[var(--twr-warm-white)]/55">Loading…</p>;

  return (
    <div data-testid="admin-loyalty-tab" className="space-y-12">
      <div>
        <h1 className="font-serif text-4xl">Loyalty</h1>
        <p className="text-[var(--twr-warm-white)]/55 mt-2">Members earn 1 point per $1 spent — shop, bar & restaurant.</p>
      </div>

      <RecordSpendForm members={members} onDone={loadAll} />
      <RedemptionsPanel redemptions={redemptions} onChange={loadAll} />
      <RewardsPanel rewards={rewards} onChange={loadAll} />
      <TransactionsPanel txns={txns} />
    </div>
  );
};

function RecordSpendForm({ members, onDone }) {
  const [form, setForm] = useState({ member_id: "", amount: "", venue: "bar", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/points/spend", { ...form, amount: parseFloat(form.amount) });
      toast.success(`+${r.data.points} points awarded · new balance ${r.data.balance.toLocaleString()}`);
      setForm({ member_id: "", amount: "", venue: "bar", note: "" });
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record spend");
    } finally { setBusy(false); }
  };

  return (
    <div className="surface p-7" data-testid="loyalty-record-spend">
      <div className="flex items-center gap-2 text-[var(--twr-bronze)]">
        <Coins size={15} strokeWidth={1.5} />
        <p className="eyebrow">Record a Bar / Restaurant Spend</p>
      </div>
      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
        <label className="block lg:col-span-2">
          <span className="eyebrow">Member *</span>
          <select data-testid="spend-member-select" required value={form.member_id} onChange={(e) => set("member_id", e.target.value)}
            className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm">
            <option value="">Select member…</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{`${m.member_no} — ${m.full_name} (${(m.points_balance || 0).toLocaleString()} pts)`}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="eyebrow">Amount (USD) *</span>
          <input data-testid="spend-amount" type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => set("amount", e.target.value)}
            className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm" />
        </label>
        <label className="block">
          <span className="eyebrow">Venue *</span>
          <select data-testid="spend-venue" value={form.venue} onChange={(e) => set("venue", e.target.value)}
            className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm">
            <option value="bar">Bar</option>
            <option value="restaurant">Restaurant</option>
            <option value="shop">Shop (in-store)</option>
          </select>
        </label>
        <button disabled={busy} data-testid="spend-submit" className="btn-bronze h-[46px]">
          {busy ? "Recording…" : "Award Points"}
        </button>
      </form>
      <input data-testid="spend-note" placeholder="Optional note — e.g. check #4402" value={form.note} onChange={(e) => set("note", e.target.value)}
        className="mt-4 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm" />
    </div>
  );
}

function RedemptionsPanel({ redemptions, onChange }) {
  const pending = redemptions.filter((r) => r.status === "pending");
  const act = async (id, action) => {
    try {
      await api.post(`/admin/redemptions/${id}/${action}`);
      toast.success(action === "fulfill" ? "Redemption fulfilled" : "Redemption cancelled — points refunded");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="loyalty-redemptions">
      <div className="flex items-center gap-2 text-[var(--twr-bronze)]">
        <Gift size={15} strokeWidth={1.5} />
        <p className="eyebrow">Redemptions {pending.length > 0 && `· ${pending.length} pending`}</p>
      </div>
      <div className="mt-5 overflow-x-auto surface">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--twr-line)] text-[var(--twr-warm-white)]/55 text-[11px] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-5 py-4">Member</th>
              <th className="text-left px-5 py-4">Reward</th>
              <th className="text-left px-5 py-4">Points</th>
              <th className="text-left px-5 py-4">Requested</th>
              <th className="text-left px-5 py-4">Status</th>
              <th className="text-right px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {redemptions.slice(0, 25).map((r) => (
              <tr key={r.id} data-testid={`redemption-row-${r.id}`} className="border-b border-[var(--twr-line)]/60 hover:bg-[var(--twr-bronze)]/5 transition-colors">
                <td className="px-5 py-4">{r.full_name} <span className="text-[var(--twr-warm-white)]/45 text-[12px]">{r.member_no}</span></td>
                <td className="px-5 py-4">{r.reward_name}</td>
                <td className="px-5 py-4 font-serif text-[var(--twr-bronze)]">{r.points_cost.toLocaleString()}</td>
                <td className="px-5 py-4 text-[var(--twr-warm-white)]/55 text-[12px]">{new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                <td className="px-5 py-4">
                  <span className={`text-[11px] tracking-[0.18em] uppercase ${r.status === "pending" ? "text-amber-400" : r.status === "fulfilled" ? "text-emerald-400" : "text-[var(--twr-warm-white)]/45"}`}>{r.status}</span>
                </td>
                <td className="px-5 py-4 text-right">
                  {r.status === "pending" && (
                    <div className="inline-flex gap-2">
                      <button onClick={() => act(r.id, "fulfill")} data-testid={`redemption-fulfill-${r.id}`} title="Fulfill" className="inline-flex items-center justify-center w-8 h-8 border border-[var(--twr-line-strong)] hover:border-emerald-400 hover:text-emerald-400 transition-colors"><Check size={13}/></button>
                      <button onClick={() => { if (window.confirm("Cancel and refund points?")) act(r.id, "cancel"); }} data-testid={`redemption-cancel-${r.id}`} title="Cancel & refund" className="inline-flex items-center justify-center w-8 h-8 border border-[var(--twr-line-strong)] hover:border-red-400 hover:text-red-400 transition-colors"><X size={13}/></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {redemptions.length === 0 && (
              <tr><td className="px-5 py-10 text-center text-[var(--twr-warm-white)]/55" colSpan={6}>No redemptions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RewardsPanel({ rewards, onChange }) {
  return (
    <div data-testid="loyalty-rewards">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="eyebrow text-[var(--twr-bronze)]">Rewards Catalog</p>
        <RewardDialog onSaved={onChange} />
      </div>
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rewards.map((r) => (
          <div key={r.id} data-testid={`reward-card-${r.id}`} className={`surface p-5 ${!r.active ? "opacity-50" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-serif truncate">{r.name}</p>
                <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55 mt-1">{r.category}{!r.active && " · inactive"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <RewardDialog reward={r} onSaved={onChange} />
                <button onClick={async () => { if (window.confirm("Delete this reward?")) { await api.delete(`/admin/rewards/${r.id}`); toast.success("Deleted"); onChange(); } }}
                  className="inline-flex items-center justify-center w-7 h-7 border border-[var(--twr-line-strong)] hover:border-red-400 hover:text-red-400 transition-colors"><Trash2 size={12}/></button>
              </div>
            </div>
            <p className="text-[var(--twr-warm-white)]/60 text-[13px] mt-3 line-clamp-2">{r.description}</p>
            <p className="font-serif text-2xl text-[var(--twr-bronze)] mt-3">{r.points_cost.toLocaleString()} <span className="text-sm">pts</span></p>
          </div>
        ))}
        {rewards.length === 0 && <p className="text-[var(--twr-warm-white)]/55 col-span-full text-center py-8">No rewards yet.</p>}
      </div>
    </div>
  );
}

function RewardDialog({ reward, onSaved }) {
  const editing = !!reward;
  const empty = { name: "", description: "", points_cost: 250, category: "drink", active: true };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm(editing ? { ...empty, ...reward } : empty); }, [open]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, points_cost: parseInt(form.points_cost) || 0 };
      if (editing) await api.put(`/admin/rewards/${reward.id}`, payload);
      else await api.post("/admin/rewards", payload);
      toast.success(editing ? "Reward updated" : "Reward created");
      onSaved(); setOpen(false);
    } catch (err) { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={editing ? `reward-edit-${reward.id}` : "admin-new-reward-btn"} className={editing ? "inline-flex items-center justify-center w-7 h-7 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)]" : "btn-bronze"}>
          {editing ? <Plus size={12}/> : <><Plus size={14}/> New Reward</>}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-[var(--twr-line)] text-[var(--twr-warm-white)] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-left">{editing ? "Edit reward" : "New reward"}</DialogTitle>
          <DialogDescription className="text-left text-[var(--twr-muted,#A1A1AA)]">{editing ? "Update this catalog reward." : "Add a reward members can redeem with points."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="eyebrow">Name *</span>
            <input data-testid="reward-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
              className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="eyebrow">Description</span>
            <textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)}
              className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="eyebrow">Points Cost *</span>
              <input data-testid="reward-points" type="number" min="1" required value={form.points_cost} onChange={(e) => set("points_cost", e.target.value)}
                className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="eyebrow">Category</span>
              <select value={form.category} onChange={(e) => set("category", e.target.value)}
                className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm">
                {["drink", "cigar", "dining", "experience"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[var(--twr-bronze)]" />
            <span className="text-[11px] tracking-[0.22em] uppercase">Active (visible to members)</span>
          </label>
          <DialogFooter><button disabled={busy} data-testid="reward-save" className="btn-bronze">{busy ? "Saving…" : (editing ? "Save" : "Create")}</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransactionsPanel({ txns }) {
  return (
    <div data-testid="loyalty-transactions">
      <p className="eyebrow text-[var(--twr-bronze)]">Recent Points Activity</p>
      <div className="mt-5 overflow-x-auto surface">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--twr-line)] text-[var(--twr-warm-white)]/55 text-[11px] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-5 py-4">Member</th>
              <th className="text-left px-5 py-4">Description</th>
              <th className="text-left px-5 py-4">Source</th>
              <th className="text-right px-5 py-4">Points</th>
              <th className="text-right px-5 py-4">When</th>
            </tr>
          </thead>
          <tbody>
            {txns.slice(0, 30).map((t) => (
              <tr key={t.id} className="border-b border-[var(--twr-line)]/60 hover:bg-[var(--twr-bronze)]/5 transition-colors">
                <td className="px-5 py-4">{t.full_name} <span className="text-[var(--twr-warm-white)]/45 text-[12px]">{t.member_no}</span></td>
                <td className="px-5 py-4 text-[var(--twr-warm-white)]/75 max-w-xs truncate" title={t.description}>{t.description}</td>
                <td className="px-5 py-4 capitalize text-[var(--twr-warm-white)]/55">{t.source}</td>
                <td className={`px-5 py-4 text-right font-serif ${t.points >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.points >= 0 ? "+" : ""}{t.points.toLocaleString()}</td>
                <td className="px-5 py-4 text-right text-[var(--twr-warm-white)]/55 text-[12px]">{new Date(t.created_at).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
              </tr>
            ))}
            {txns.length === 0 && (
              <tr><td className="px-5 py-10 text-center text-[var(--twr-warm-white)]/55" colSpan={5}>No points activity yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
